/* eslint-disable react/no-unescaped-entities */
/* eslint-disable no-irregular-whitespace */
'use client';

import { useState } from 'react';

import { Locale } from '@core/Entities/Locale/Locale.entity';
import { Vehicle } from '@core/Entities/Vehicle/Vehicle.entity';
import { Dropdown } from '@gds/Dropdown/Dropdown';
import { Close } from '@gds/Icons/Paths/Close';
import { Input } from '@gds/Input/Input';
import { Slider } from '@gds/Slider/Slider';

import { chargingDefaults } from './ChargingDefaults';
import { useMinValue } from './Utils/useMinValue';

import { vehicleCategories } from './VehicleCategories';

import styles from './EvSpecsModal.module.css';

type EvSpecsModalClientProps = {
  vehicle: Vehicle;
  locale: Locale;
  tenant: any;
  strings: Record<string, string>;
};

export const EvSpecsModalClient = ({ vehicle, strings }: EvSpecsModalClientProps) => {
  const closeModal = () => document.body.removeAttribute('data-is-ev-compare-modal-open');

  const [kmPerDay, inputKmPerDay, setKmPerDay] = useMinValue(50, 1, 500);
  const [pricePerKwh, inputPricePerKwh, setPricePerKwh] = useMinValue(0.2, 0.01, 10);
  const [fuelPricePerLitre, inputFuelPricePerLitre, setFuelPrice] = useMinValue(1.75, 0.01, 10);

  const sameCatIndex = vehicleCategories.findIndex(cat => cat.value === vehicle.spec.bodyType) ?? 0;

  const [selectedVehicleCategory, setSelectedVehicleCategory] = useState(
    vehicleCategories[sameCatIndex],
  );
  const [fuelLitresPer100Km, setFuelLitresPer100Km] = useState(
    vehicleCategories[0].defaultConsumption,
  );
  const [whPerKm, _setWhPerKm] = useState(vehicle?.evData?.consumptionWltpWhPerKm ?? 190);

  const evCostPerMonth = kmPerDay * (whPerKm / 1000) * pricePerKwh * 30;
  const evCostPerYear = evCostPerMonth * 12;

  const iceCostPerMonth = ((kmPerDay * fuelLitresPer100Km * fuelPricePerLitre) / 100) * 30;
  const iceCostPerYear = iceCostPerMonth * 12;

  const potentialSaving = ((iceCostPerYear - evCostPerYear) / iceCostPerYear) * 100;

  const asPrice = (value: number) => value.toFixed(0).toLocaleString();

  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.modal}>
          <div className={styles.header}>
            {strings.modalTitle} <Close onClick={closeModal} tabIndex={0} />
          </div>
          <div className={styles.content}>
            <div className={styles.description}>{strings.description}</div>
            <div className={styles.verticalBar} />
            <div className={styles.horizontalBar} />

            <div className={styles.currentVehicle}>
              <div className={styles.currentVehicleTitle}>
                {vehicle.evData?.make && vehicle.evData?.model
                  ? `${vehicle.evData.make} ${vehicle.evData.model}`
                  : vehicle.prettyName}
              </div>
              <div className={styles.costLabel}>{strings.annualCost}</div>
              <div className={styles.costValue} key={evCostPerYear}>
                {asPrice(evCostPerYear)} €
              </div>
              <div className={styles.costLabel}>{strings.monthlyCost}</div>
              <div className={styles.costValue} key={evCostPerMonth}>
                {asPrice(evCostPerMonth)} €
              </div>
              <div className={styles.costLabel}>{strings.potentialSavings}</div>
              <div className={styles.costValueSmall} key={potentialSaving}>
                {potentialSaving.toFixed(0)}%
              </div>
            </div>

            <div className={styles.iceVehicle}>
              <div className={styles.iceVehicleTitle}>{strings.yourVehicle}</div>

              <Dropdown
                className={styles.dropdown}
                label={strings.vehicleType}
                options={vehicleCategories}
                value={selectedVehicleCategory}
                onChange={(option: (typeof vehicleCategories)[number]) => {
                  setSelectedVehicleCategory(option);
                  setFuelLitresPer100Km(option.defaultConsumption);
                }}
              />
              <div className={styles.costLabel}>{strings.annualCost}</div>
              <div className={styles.costValue} key={iceCostPerYear}>
                {asPrice(iceCostPerYear)} €
              </div>
              <div className={styles.costLabel}>{strings.monthlyCost}</div>
              <div className={styles.costValue} key={iceCostPerMonth}>
                {asPrice(iceCostPerMonth)} €
              </div>
            </div>

            <div className={styles.inputWrapper}>
              <Input
                className={styles.input}
                label={strings.distancePerDay}
                value={inputKmPerDay}
                onChange={e => setKmPerDay(e.target.value)}
                rightAddons="km"
              />
            </div>
            <Slider
              className={styles.slider}
              step={1}
              stepCount={201}
              min={1}
              selectedRangeIndexes={[kmPerDay]}
              isSingleKnob
              onChange={([value]) => setKmPerDay(value)}
            />

            <div className={styles.inputWrapper}>
              <Input
                className={styles.input}
                label={strings.fuelCost}
                value={inputFuelPricePerLitre}
                onChange={e => setFuelPrice(e.target.value)}
                rightAddons="€/L"
              />
            </div>
            <Slider
              className={styles.slider}
              step={0.01}
              stepCount={5}
              min={0.01}
              selectedRangeIndexes={[fuelPricePerLitre]}
              isSingleKnob
              onChange={([value]) => setFuelPrice(value)}
            />

            <Dropdown
              className={styles.dropdown}
              label={strings.chargingType}
              options={chargingDefaults}
              value={chargingDefaults[0]}
              onChange={(option: (typeof chargingDefaults)[number]) =>
                setPricePerKwh(option.defaultPrice)
              }
            />

            <div className={styles.inputWrapper}>
              <Input
                className={styles.input}
                label={strings.electricityCost}
                value={inputPricePerKwh}
                rightAddons="€/kWh"
                onChange={e => setPricePerKwh(e.target.value)}
              />
            </div>
            <Slider
              className={styles.slider}
              step={0.01}
              stepCount={3}
              min={0.01}
              selectedRangeIndexes={[pricePerKwh]}
              isSingleKnob
              onChange={([value]) => setPricePerKwh(value)}
            />

            <div className={styles.disclaimer}>
              {strings.disclaimer &&
                strings.disclaimer
                  .replace('{consumption}', String(vehicle.evData?.consumptionWltpWhPerKm ?? 190))
                  .replace('{fuelConsumption}', fuelLitresPer100Km.toFixed(1))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
